import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { useCanManageOrgMembers } from '@/features/org/hooks/use-can-manage-org-members';
import { useCurrentOrg } from '@/features/org/hooks/use-org';
import { CreateProjectCreatingProject } from '@/features/projects/create-project/components/forms/create-project-creating-project';
import { CreateProjectInfoForm } from '@/features/projects/create-project/components/forms/create-project-info-form';
import { CreateProjectInvitationInputForm } from '@/features/projects/create-project/components/forms/create-project-invitation-input-form';
import { CreateProjectInvitationOverviewForm } from '@/features/projects/create-project/components/forms/create-project-invitation-overview-form';
import { CreateProjectKpiForm } from '@/features/projects/create-project/components/forms/create-project-kpi-form';
import { CreateProjectKpiSummaryForm } from '@/features/projects/create-project/components/forms/create-project-kpi-summary-form';
import { CreateProjectSelectBranchForm } from '@/features/projects/create-project/components/forms/create-project-select-branch-form';
import { CreateProjectHeader } from '@/features/projects/create-project/components/layouts/create-project-header';
import { CreateProjectLayout } from '@/features/projects/create-project/components/layouts/create-project-layout';
import { useCreateProjectMutation } from '@/features/projects/create-project/hooks/use-create-project-mutation';
import { useOrgMembersForProject } from '@/features/projects/create-project/hooks/use-org-members-for-project';
import { useCreateProjectStore } from '@/features/projects/create-project/store/use-create-project-store';
import { getProjectsQueryKey } from '@/features/projects/shared/hooks/use-projects';
import { FlowSkipStepButton } from '@/shared/components/flow/flow-actions';
import { toast } from '@/shared/hooks/use-toast';

export const CreateProjectPage = () => {
  const { t } = useTranslation('project', {
    keyPrefix: 'CreateProjectSubmitPage',
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canManage = useCanManageOrgMembers();
  const { data: currentOrg, isLoading: isOrgLoading } = useCurrentOrg();

  const { data, step: activeStep, setData, reset } = useCreateProjectStore();
  const memberIdsToInvite = data.memberIdsToInvite || [];

  useEffect(() => {
    if (!canManage) {
      navigate(paths.projects.hub.getHref(), { replace: true });
    }
  }, [canManage, navigate]);

  useEffect(() => {
    reset();
  }, []);

  const { mutate, isPending: isSubmitting } = useCreateProjectMutation({
    onMutate: () => {
      setData({ step: 'submitting' });
    },
    onSuccess: ({ projectId }) => {
      queryClient.invalidateQueries({
        queryKey: getProjectsQueryKey(currentOrg?.id),
      });
      navigate(paths.projects.details.base.getHref(projectId.toString()));
    },
    onError: () => {
      toast({
        title: 'An error occurred',
        description: 'Something went wrong while creating the project',
      });
      setData({ step: 'projectInfo' });
    },
  });

  // Cancel is handled by the CreateProjectHeader component
  // which includes a cancel button that navigates to dashboard
  // and resets the store state

  const handleSubmitProject = () => {
    if (!currentOrg?.id) {
      toast({
        title: t('org_not_found'),
      });
      setData({ step: 'projectInfo' });
      return;
    }

    if (!data.projectName) {
      setData({ step: 'projectInfo' });
      return;
    }

    mutate({ org_id: currentOrg.id, data });
  };

  const handleAddInvitation = (memberId: string) => {
    const updatedInvitations = [...memberIdsToInvite, memberId];
    setData({ memberIdsToInvite: updatedInvitations });

    // Move to overview after adding first invitation
    if (memberIdsToInvite.length === 0) {
      setData({ step: 'addUsersOverview' });
    }
  };

  const handleRemoveInvitation = (memberId: string) => {
    const updatedInvitations = memberIdsToInvite.filter(
      (invitation) => invitation !== memberId,
    );
    setData({ memberIdsToInvite: updatedInvitations });
  };

  const { data: orgMembers } = useOrgMembersForProject();

  return (
    <CreateProjectLayout>
      {activeStep === 'projectInfo' && (
        <CreateProjectInfoForm
          onChange={setData}
          onSuccess={() => {
            setData({ step: 'selectBranch' });
          }}
        />
      )}
      {activeStep === 'selectBranch' && (
        <CreateProjectSelectBranchForm
          onSuccess={() => {
            setData({ step: 'createKpi' });
          }}
          onBack={() => {
            setData({ step: 'projectInfo' });
          }}
          onChange={setData}
        />
      )}
      {activeStep === 'createKpi' && (
        <CreateProjectKpiForm
          onSuccess={() => {
            setData({ step: 'kpiSummary' });
          }}
          onSkip={() => {
            if (data.kpis?.length && data.kpis.length > 0) {
              setData({ step: 'kpiSummary' });
            } else {
              setData({ step: 'addUsersOverview' });
            }
          }}
          onBack={() => {
            setData({ step: 'selectBranch' });
          }}
          onChange={setData}
        />
      )}
      {activeStep === 'kpiSummary' && (
        <CreateProjectKpiSummaryForm
          onSuccess={() => {
            setData({ step: 'addUsersOverview' });
          }}
          onBack={() => {
            setData({ step: 'createKpi' });
          }}
          onChange={setData}
          onAddMore={() => {
            setData({ step: 'createKpi' });
          }}
          onSkip={() => {
            setData({ step: 'addUsersOverview' });
          }}
        />
      )}
      {activeStep === 'addUsers' && (
        <>
          <CreateProjectHeader>
            <FlowSkipStepButton
              onClick={() => {
                if (memberIdsToInvite.length > 0) {
                  setData({ step: 'addUsersOverview' });
                } else {
                  setData({ step: 'submitting' });
                  handleSubmitProject();
                }
              }}
            />
          </CreateProjectHeader>
          <CreateProjectInvitationInputForm
            onSuccess={handleAddInvitation}
            onSkip={() => {
              if (memberIdsToInvite.length > 0) {
                setData({ step: 'addUsersOverview' });
              } else {
                setData({ step: 'submitting' });
                handleSubmitProject();
              }
            }}
            memberIdsToInvite={memberIdsToInvite}
            onBack={() => {
              setData({ step: 'addUsersOverview' });
            }}
            orgMembers={orgMembers || []}
          />
        </>
      )}
      {activeStep === 'addUsersOverview' && (
        <>
          <CreateProjectHeader>
            <FlowSkipStepButton
              onClick={() => {
                setData({ step: 'submitting' });
                handleSubmitProject();
              }}
            />
          </CreateProjectHeader>
          <CreateProjectInvitationOverviewForm
            memberIdsToInvite={memberIdsToInvite}
            orgMembers={orgMembers || []}
            onAddMore={() => {
              setData({ step: 'addUsers' });
            }}
            onBack={() => {
              if (data.kpis?.length && data.kpis.length > 0) {
                setData({ step: 'kpiSummary' });
              } else {
                setData({ step: 'createKpi' });
              }
            }}
            onSubmitAll={() => {
              setData({ step: 'submitting' });
              handleSubmitProject();
            }}
            handleRemove={handleRemoveInvitation}
          />
        </>
      )}
      {activeStep === 'submitting' && (
        <CreateProjectCreatingProject
          isLoading={isSubmitting || isOrgLoading}
          error={
            !currentOrg && !isOrgLoading ? 'Organization not found' : undefined
          }
        />
      )}
    </CreateProjectLayout>
  );
};
